/**
 * Plain-language bullet extraction for the WhyPanel "What I saw" section.
 *
 * Split out of WhyPanel.tsx so the component file exports only React
 * components (react-refresh fast-refresh requirement). The unit test also
 * imports it from here without going through the component module.
 */

export interface WhatISawBullet {
  key: string;
  bullet: string;
  rawScore?: number;
}

/**
 * Build up to five plain-language bullets from a free-shape signals.perSignal
 * payload.
 *
 * Priority: rule → text → vision → neighbour → anything else. Each bullet
 * pulls from `reason` when present; otherwise it falls back to a generic
 * description tied to the score.
 */
export function buildWhatISawBullets(
  perSignal: Record<string, unknown>,
): readonly WhatISawBullet[] {
  const orderedKeys = ['rule', 'text', 'vision', 'neighbour', 'neighbor'];
  const seen = new Set<string>();
  const bullets: WhatISawBullet[] = [];

  const collect = (key: string) => {
    if (seen.has(key)) return;
    const entry = perSignal[key];
    if (entry === null || entry === undefined) return;
    seen.add(key);

    if (typeof entry === 'object') {
      const obj = entry as Record<string, unknown>;
      const reason = typeof obj.reason === 'string' ? obj.reason : null;
      const score = typeof obj.score === 'number' ? obj.score : undefined;
      if (reason) {
        bullets.push({ key, bullet: reason, rawScore: score });
        return;
      }
      if (typeof score === 'number') {
        bullets.push({
          key,
          bullet: `${humanLabel(key)} signal contributed.`,
          rawScore: score,
        });
        return;
      }
    }
    if (typeof entry === 'string') {
      bullets.push({ key, bullet: entry });
    }
  };

  for (const k of orderedKeys) collect(k);
  for (const k of Object.keys(perSignal)) collect(k);

  return bullets.slice(0, 5);
}

function humanLabel(key: string): string {
  switch (key) {
    case 'rule':
      return 'Rule';
    case 'text':
      return 'Title';
    case 'vision':
      return 'Screenshot';
    case 'neighbour':
    case 'neighbor':
      return 'Neighbour';
    default:
      return key.charAt(0).toUpperCase() + key.slice(1);
  }
}
